import { MenuIcon, MultiSelect, Prompt, SearchField, Table, useSnackbar } from '@weareopenr/component-library';
import EmptyState from '../../components/EmptyState';
import Burger from '../../assets/images/Burger';
import { useNavigate } from 'react-router-dom';
import { Routes } from '../../routes';
import styles from './PriceSchemePricingTab.module.scss';
import { useCallback, useMemo, useRef, useState } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import { useForm } from 'react-hook-form';
import { PriceInput } from '../PricingTablePriceInput/PricingTablePriceInput';
import { useProductPrices } from '../../hooks/useProductPrices';
import { downStreamExternalSystemIntegrationTypes, PAGE_FOOTER_ID, PRICING_FORM_ID } from '../../constants';
import SkeletonContainer from '../../components/SkeletonContainer';
import { usePriceBands } from 'src/hooks/usePriceBands';
import { emptySectionButtonText, emptySectionText } from './constants';
import { DELIMITER, generatePriceFormDefaults, submitPrices } from '../../helpers/priceTable';
import { PricingSaveSummary } from '../PricingSaveSummary/PricingSaveSummary';
import { IProductPrice } from 'src/types/productPricing.types';
import { useSetDefaults } from '../../hooks/useProductPrices/useProductPrices';
import { createPortal } from 'react-dom';
import { useDebounce } from '../../hooks/useDebounce/useDebounce';
import { useRules } from '../../hooks/useRules/useRules';
import { useExternalSystems } from 'src/hooks/useExternalSystems/useExternalSystems';
import { useQueryClient } from '@tanstack/react-query';
import { useMenus } from 'src/hooks/useMenus';
import { useCancelCheck } from 'src/hooks/useCancelCheck';
import { NoResultsEmptyState } from '../NoResultsEmptyState';

type PricingTableRow = {
  name: string;
  portion: string;
  itemId: string;
  priceBandId: string | null;
  ranges?: string;
  priceBands?: PricingTableRow[];
};

const columnHelper = createColumnHelper<PricingTableRow>();

const PriceCell = ({
  itemId,
  priceBandId,
  aggregatorId = null,
  control,
  register,
  priceRules,
  tableContainerRef,
}: {
  itemId: string;
  priceBandId: string | null;
  aggregatorId?: string | null;
  control: any;
  register: any;
  priceRules: any;
  tableContainerRef: React.RefObject<HTMLDivElement>;
}) => {
  const inputName = `${itemId}${DELIMITER}${aggregatorId}${DELIMITER}${priceBandId}`;
  return (
    <PriceInput
      inputName={inputName}
      control={control}
      register={register}
      priceRules={priceRules}
      tableContainerRef={tableContainerRef}
    />
  );
};

const PriceSchemePricingTab = ({ priceSchemeId }: { priceSchemeId: string }) => {
  const { data: externalSystems } = useExternalSystems();
  const { priceBands, isLoading: isPriceBandsLoading } = usePriceBands(priceSchemeId);
  const { data: priceRules, isLoading: isRulesLoading } = useRules(priceSchemeId);

  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState<string>('');
  const [searchMenus, setSearchMenus] = useState<string[]>([]);
  const debouncedSearchValue = useDebounce(searchValue);
  const { data: menus } = useMenus();

  const queryClient = useQueryClient();

  const downstreamExternalSystems = useMemo(() => {
    return (
      externalSystems?.filter((aggregator) =>
        downStreamExternalSystemIntegrationTypes.includes(aggregator.integrationType),
      ) || []
    );
  }, [externalSystems]);

  const fetchLimit = 50;
  const cacheTimeToClearOnUnmount = 0;
  const {
    data,
    isLoading: isPricesLoading,
    isFetchingNextPage,
    queryKey,
    hasNextPage,
    fetchNextPage,
  } = useProductPrices(priceSchemeId, fetchLimit, {
    cacheTime: cacheTimeToClearOnUnmount,
    searchParam: debouncedSearchValue,
    menus: searchMenus,
  });

  const products = useMemo(() => data?.pages.flatMap((page) => page.data.map((product) => product)), [data]);

  const tableContainerRef = useRef<HTMLDivElement>(null);

  const defaultValues = useMemo(() => {
    return generatePriceFormDefaults(products, priceBands, downstreamExternalSystems);
    // Default values can only be set on initial render, so we can safely ignore the dependencies here
  }, []);

  const pricingForm = useForm<Record<string, string>>({ defaultValues });

  const onNewProducts = useCallback(
    (products: IProductPrice[]) => {
      const defaults = generatePriceFormDefaults(products, priceBands, downstreamExternalSystems);
      pricingForm.reset(defaults, { keepValues: true, keepDirtyValues: true });
    },
    [priceBands, downstreamExternalSystems],
  );

  useSetDefaults(onNewProducts, queryKey);

  const tableData: PricingTableRow[] = products?.map((item) => {
    return {
      name: item.name,
      itemId: item.item_id,
      priceBandId: null,
      portion: item.product_size?.name,
      ranges: item.ranges?.map((x) => x.name).join(', ') || '',
      priceBands: priceBands
        .filter((priceBand) => priceBand.name !== 'Default')
        .map((priceBand) => {
          return {
            name: priceBand.name,
            itemId: item.item_id,
            priceBandId: priceBand.priceBandId,
            portion: item.product_size?.name,
            ranges: item.ranges?.map((x) => x.name).join(', ') || '',
          };
        }),
    };
  });

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        size: 250,
        header: 'Product',
      }),
      columnHelper.accessor('portion', {
        size: 100,
        header: 'Portion',
      }),
      columnHelper.accessor('ranges', {
        size: 200,
        header: 'Ranges',
      }),
      columnHelper.display({
        header: 'Default',
        size: 150,
        cell: (info) => {
          const { itemId, priceBandId } = info.row.original;
          return (
            <PriceCell
              itemId={itemId}
              priceBandId={priceBandId}
              control={pricingForm.control}
              register={pricingForm.register}
              priceRules={priceRules}
              tableContainerRef={tableContainerRef}
            />
          );
        },
      }),
      ...downstreamExternalSystems.map((aggregator) => {
        return columnHelper.display({
          header: aggregator.name,
          size: 150,
          cell: (info) => {
            const { itemId, priceBandId } = info.row.original;
            return (
              <PriceCell
                itemId={itemId}
                priceBandId={priceBandId}
                aggregatorId={aggregator.aggregatorId}
                control={pricingForm.control}
                register={pricingForm.register}
                priceRules={priceRules}
                tableContainerRef={tableContainerRef}
              />
            );
          },
        });
      }),
    ],
    [downstreamExternalSystems, priceRules],
  );

  const columnHeadersToRightAlign = useMemo(() => {
    return [...downstreamExternalSystems.map((aggregator) => aggregator.name), 'Default'];
  }, [downstreamExternalSystems]);

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: queryKey });
  };

  const onSubmit = submitPrices(
    pricingForm.formState.dirtyFields,
    products,
    pricingForm.reset,
    showSnackbar,
    invalidate,
    priceSchemeId,
  );

  const totalChanges = Object.keys(pricingForm.formState.dirtyFields).length;
  const isLoadingData = isPricesLoading || isRulesLoading || isPriceBandsLoading;

  const emptyStateText = emptySectionText;

  const { isCancelCheck, message, handleStay, handleLeave } = useCancelCheck({
    totalChanges,
  });

  return (
    <div className={styles['price-scheme-pricing-tab']}>
      <Prompt
        closePrompt={handleStay}
        open={isCancelCheck}
        buttonHandler={handleLeave}
        buttonText="Leave page"
        backButton
        backButtonText="Review changes"
        promptHeading="Unsaved changes"
        size="small"
      >
        {message}
      </Prompt>
      <div className={styles['search-container']}>
        <SearchField
          placeholder={'Search'}
          onChange={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setSearchValue(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
            }
          }}
          isDisabled={false}
        />
        {menus?.length > 0 && (
          <MultiSelect
            Icon={MenuIcon}
            label="Menus"
            options={menus?.map((menu, index) => ({ id: menu.menuId, label: menu.name, display_order: index })) || []}
            selected={searchMenus}
            onChange={setSearchMenus}
            disabled={menus?.length === 0}
          />
        )}
      </div>

      <form id={PRICING_FORM_ID} className={styles['form-container']} onSubmit={pricingForm.handleSubmit(onSubmit)}>
        {isLoadingData ? (
          <SkeletonContainer hiddenTitle />
        ) : (
          <>
            {products?.length === 0 && !debouncedSearchValue && searchMenus.length === 0 ? (
              <EmptyState
                emptySectionText={emptyStateText}
                buttonText={emptySectionButtonText}
                onClick={() => navigate(Routes.PRODUCT_LIBRARY)}
                Icon={Burger}
              />
            ) : (
              <div className={styles['table-container']}>
                <Table
                  // className={styles['table-fill-height']}
                  data={tableData}
                  columns={columns}
                  columnsToPin={['name']}
                  columnHeadersToRightAlign={columnHeadersToRightAlign}
                  getSubRows={(originalRow) => originalRow.priceBands}
                  {...((debouncedSearchValue || searchMenus.length > 0) && {
                    EmptyState: () => <NoResultsEmptyState className={styles['no-results']} />,
                  })}
                  isFetchingNextPage={isFetchingNextPage}
                  hasNextPage={hasNextPage}
                  fetchNextPage={fetchNextPage}
                />
              </div>
            )}
          </>
        )}

        {totalChanges > 0 &&
          createPortal(
            <PricingSaveSummary
              reset={pricingForm.reset}
              totalChanges={totalChanges}
              isSubmitting={pricingForm.formState.isSubmitting}
            />,
            document.getElementById(PAGE_FOOTER_ID),
          )}
      </form>
    </div>
  );
};

export default PriceSchemePricingTab;
